/**
 * The project workspace's tabs (design "Flow Project Tabs", utils/projectTabsModel.js has which ones a person sees):
 * Home · Preview · Code, the advanced tabs on the row (API, Data, Traces, Quality: pinned, shown by themselves, or
 * opened for now), then History (Changes, Activity and Sessions as three views of one tab, always), and More.
 * On a phone: Home · Preview · Code · History · More. Home (id 'dashboard', its old name: `?tab=dashboard` and
 * `?tab=home` both land on the page), Code and Preview are the project's own pages (Home, a service's editor or its
 * preview); API (try the services' APIs, components/api), Data (the project's datastores, components/data), Traces
 * (how the project's services talk to each other, from the requests: components/traces), Quality (tests, lint,
 * vulnerabilities, secrets, licences and the release gate: components/quality), Terminal (shells in the sandbox,
 * components/terminal/TerminalTabView), Git (each service's repo: commit, tag, push, pull, components/git/GitTab), Changes, Activity and
 * Sessions are workspace views shown in place of them (`view`): the old History dialog's Timeline and By person are
 * the two views of Changes, its Activity is the Activity view.
 *
 * A view is `{ tab: 'api' | 'mcp' | 'data' | 'traces' | 'quality' | 'config' | 'mail' | 'terminal' | 'git' | 'changes' | 'activity' | 'sessions', sub?: 'person' |
 * 'timeline' | 'versions', focus? }` (Data's focus: `{ store, object? }`, the store to open, or `{ view: 'restore', point? }`, its Restore points). Links:
 * `?tab=api|data|traces|jobs|finops|quality|config|mail|terminal|git|changes|activity|sessions` (the old `?tab=config&focus=jobs`
 * opens Jobs)
 * Each view keeps its selected element in its own params (utils/projectTabUrl TAB_PARAMS: Data's `&store=&table=`,
 * Traces' `&trace=&span=`, Apps' `&service=&section=`, Files' `&path=` (the old `&file=` read too), Quality's `&check=`,
 * …): a link's become the view's focus (focusOfParams), the focus is written back (paramsOfView), and the panel writes
 * what the person picks with replace (hooks/useTabSelectionUrl); the project's own pages are `?tab=home|code|logs`,
 * utils/projectTabUrl), `?tab=history` (History on its last view; Changes' view in `&sub=`), and the old
 * `?history=person|timeline|activity` still lands on the same place.
 */
import { HISTORY_IDS, lastHistorySub } from './projectTabsModel';
import { cleanFilePath, serviceOfFile } from './filesPath';
import { TAB_OWN_PARAMS, paramsOfTab, searchForHomeMode, searchOf, tabParamsFromSearch } from './projectTabUrl';

/** The default row, in order (History stands for Changes, Activity and Sessions). */
export const WORKSPACE_TABS = [
  { id: 'dashboard', label: 'Home' },
  { id: 'preview', label: 'Preview' },
  { id: 'code', label: 'Code' },
  { id: 'history', label: 'History' },
];

export const VIEW_TABS = ['apps', 'files', 'api', 'mcp', 'data', 'traces', 'jobs', 'finops', 'quality', 'config', 'mail', 'terminal', 'git', 'changes', 'activity', 'sessions'];

/**
 * A view as it is shown today: Configuration on its scheduled jobs (`{ tab: 'config', focus: { section: 'jobs' } }`,
 * the old Operate › Jobs, kept by older links and events) is the Jobs tab, on that service if one was named.
 */
export function normalizeView(view) {
  if (view?.tab === 'config' && view.focus?.section === 'jobs') {
    return { tab: 'jobs', sub: undefined, focus: view.focus.service ? { service: view.focus.service } : null };
  }
  return view;
}
export const CHANGES_SUBS = [
  { id: 'person', label: 'By person' },
  { id: 'timeline', label: 'Timeline' },
  { id: 'versions', label: 'Versions' },
];
const CHANGES_SUB_IDS = CHANGES_SUBS.map((s) => s.id);

const SUB_KEY = 'flow.changes.view';

/**
 * Opens a workspace view from anywhere (Canvas listens): `{ tab, focus }` as viewFromHistory reads them, `{}` for
 * Changes on its last view.
 */
export const openHistory = (target = {}) => {
  if (typeof window !== 'undefined') { window.dispatchEvent(new CustomEvent('collab:openHistory', { detail: target })); }
};

/** The key that tells two views apart for Back / Forward: the tab, its sub-view and the Files tab's file. */
export const viewKey = (view) => [view?.tab || '', view?.sub || '', view?.tab === 'files' ? view.focus?.path || '' : ''].join('|');

/**
 * Opens a project file where it belongs (the Home's File gadget, a Markdown link in it): a file of a service in Code on
 * that service, with the file selected (`flow:open-code`, UnifiedTopBar: `?view=files&path=…`, which Canvas turns into
 * the service's Code tab); any other file (the Brain, inputs, results, the root's README) in the Files tab's file view.
 * `filesTab` false (the plan hides Files): Code for every file. Never a folder opener: a file path given as the root of
 * a file tree is listed as a folder and fails ("Not a directory").
 */
export function openProjectFile(path, { filesTab = true } = {}) {
  const file = cleanFilePath(path);
  if (!file || typeof window === 'undefined') { return; }
  if (serviceOfFile(file) || !filesTab) {
    window.dispatchEvent(new CustomEvent('flow:open-code', { detail: { path: file } }));
    return;
  }
  openHistory({ tab: 'files', focus: { path: file } });
}

/** The Changes view last used in this browser: 'person' (default), 'timeline' or 'versions'. */
export function lastChangesSub() {
  try {
    const saved = localStorage.getItem(SUB_KEY);
    return CHANGES_SUB_IDS.includes(saved) ? saved : 'person';
  } catch {
    return 'person';
  }
}

export function rememberChangesSub(sub) {
  try { localStorage.setItem(SUB_KEY, CHANGES_SUB_IDS.includes(sub) ? sub : 'person'); } catch { /* not remembered */ }
}

/**
 * The view an old History request asks for: `{ tab: 'person' | 'timeline' | 'versions' | 'activity' | null, focus }` (the
 * `collab:openHistory` event, `?history=`, the project Home's What changed). No tab: Changes, on its last view.
 */
export function viewFromHistory(tab, focus = null) {
  if (tab === 'history') { return viewFromHistory(lastHistorySub(), focus); }
  if (tab === 'activity') { return { tab: 'activity', focus: focus || null }; }
  if (CHANGES_SUB_IDS.includes(tab)) { return { tab: 'changes', sub: tab, focus: focus || null }; }
  if (VIEW_TABS.includes(tab)) { return normalizeView({ tab, sub: tab === 'changes' ? lastChangesSub() : undefined, focus: focus || null }); }
  return { tab: 'changes', sub: lastChangesSub(), focus: focus || null };
}

/** Quality's own pages (QualityPanel `view.name`), besides a check's page: `?tab=quality&check=<page>`. */
const QUALITY_PAGES = ['tests', 'settings', 'run', 'coverage', 'provenance', 'assessments', 'compliance'];
const text = (value) => (typeof value === 'string' && value ? value : null);

/**
 * A view's element as its tab's params (utils/projectTabUrl TAB_PARAMS), `{ [param]: value | null }`: what a link to
 * it, or the view opened on it, writes in the URL. Changes writes its view too (`sub`: person, timeline, versions).
 */
export function paramsOfView(view) {
  const focus = view?.focus || {};
  switch (view?.tab) {
    case 'files': return { path: cleanFilePath(focus.path) || null };
    case 'apps': return { service: text(focus.service), section: focus.section === 'overview' ? null : text(focus.section) };
    case 'data':
      if (focus.view === 'restore') { return { restore: text(focus.point) || 'all' }; }
      return { store: text(focus.store), table: text(focus.store) ? text(focus.object) : null };
    case 'quality': {
      const page = focus.sub === 'check' ? text(focus.check) : focus.sub && focus.sub !== 'overview' ? text(focus.sub) : null;
      return { service: text(focus.service), check: page, finding: page ? text(focus.id) || text(focus.path) || text(focus.framework) : null };
    }
    case 'changes': return { commit: text(focus.sha) || (focus.shas?.length ? focus.shas.filter(Boolean).join(',') || null : null) };
    default: {
      const out = {};
      paramsOfTab(view?.tab).forEach((key) => { out[key] = text(focus[key]); });
      return out;
    }
  }
}

/** The focus a tab's params ask for (the reverse of paramsOfView), null when they name nothing. */
export function focusOfParams(tab, values = {}) {
  const v = values || {};
  let focus = null;
  switch (tab) {
    case 'files': { const path = cleanFilePath(v.path) || cleanFilePath(v.file); focus = path ? { path } : null; break; }
    case 'data':
      if (v.restore) { focus = { view: 'restore', ...(v.restore !== 'all' ? { point: v.restore } : {}) }; } else if (v.store) { focus = { store: v.store, ...(v.table ? { object: v.table } : {}) }; }
      break;
    case 'quality': {
      focus = {};
      if (v.service) { focus.service = v.service; }
      if (v.check && QUALITY_PAGES.includes(v.check)) {
        focus.sub = v.check;
        if (v.finding) { focus[v.check === 'coverage' ? 'path' : v.check === 'assessments' ? 'framework' : 'id'] = v.finding; }
      } else if (v.check) { focus.sub = 'check'; focus.check = v.check; }
      break;
    }
    case 'changes': {
      // One commit (the Timeline's open one) or the changes a link from Activity showed (`a,b`).
      const shas = String(v.commit || '').split(',').map((sha) => sha.trim()).filter(Boolean);
      focus = shas.length ? { sha: shas[0], shas } : null;
      break;
    }
    default:
      focus = {};
      paramsOfTab(tab).forEach((key) => { if (v[key]) { focus[key] = v[key]; } });
  }
  return focus && Object.keys(focus).length ? focus : null;
}

/**
 * The view a URL asks for (`?tab=` first, then the old `?history=`), or null, on the element its params name
 * (focusOfParams: Apps' service and sub-tab, Files' file, Data's store and table, Quality's check, …; the old `&file=`
 * too). Changes on the view in `&sub=` (else its last one).
 */
export function viewFromSearch(search) {
  const params = new URLSearchParams(search || '');
  const tab = params.get('tab');
  // An older link to Configuration's jobs (`?tab=config&focus=jobs`, or `&section=jobs`): the Jobs tab.
  if (tab === 'config' && (params.get('focus') === 'jobs' || params.get('section') === 'jobs')) { return viewFromHistory('jobs'); }
  if (VIEW_TABS.includes(tab)) {
    const values = tabParamsFromSearch(search, tab);
    if (tab === 'files' && params.get('file')) { values.file = params.get('file'); }
    const focus = focusOfParams(tab, values);
    if (tab === 'changes' && CHANGES_SUB_IDS.includes(params.get('sub'))) { return { tab, sub: params.get('sub'), focus }; }
    return viewFromHistory(tab, focus);
  }
  if (tab === 'history') { return viewFromHistory(tab); }
  const history = params.get('history');
  if (history) { return viewFromHistory(history); }
  return null;
}

/**
 * The search string with the view written in (`?tab=`), the old `?view=` and `?history=` dropped. Every other tab's
 * params (utils/projectTabUrl TAB_OWN_PARAMS) go: the view writes its own from its focus (paramsOfView: a link to an
 * element), else keeps the ones its panel wrote while it stays on (the same tab), else none. Files' file is its focus
 * only (Up drops it); Traces' `trace` written just before Traces opens (the Home's Errors gadget) stays.
 * No view (the page): a workspace tab and its params go. `{ homeMode }` given (a view was just closed): the page's
 * Home tab is written too (`?tab=home|code|logs`, searchForHomeMode), or none when a service is shown (null).
 */
export function searchWithView(search, view, options = {}) {
  if (!view && Object.prototype.hasOwnProperty.call(options, 'homeMode')) {
    return searchForHomeMode(dropWorkspaceTab(search), options.homeMode);
  }
  if (!view) { return dropWorkspaceTab(search); }
  const params = new URLSearchParams(search || '');
  // Another view of Changes is another page: the commit picked in the last one goes.
  const sameTab = params.get('tab') === view.tab && (view.tab !== 'changes' || (params.get('sub') || null) === (view.sub || null));
  const fromFocus = paramsOfView(view);
  const asked = Object.values(fromFocus).some(Boolean);
  const values = asked || view.tab === 'files' ? { ...fromFocus } : sameTab ? tabParamsFromSearch(search, view.tab) : {};
  if (view.tab === 'traces' && !values.trace && params.get('trace')) { values.trace = params.get('trace'); }
  ['history', 'view', 'sub', ...TAB_OWN_PARAMS].forEach((key) => params.delete(key));
  if (params.get('focus') === 'jobs') { params.delete('focus'); }
  paramsOfTab(view.tab).forEach((key) => { if (values[key]) { params.set(key, values[key]); } });
  if (view.tab === 'changes' && CHANGES_SUB_IDS.includes(view.sub)) { params.set('sub', view.sub); }
  if (VIEW_TABS.includes(view.tab)) { params.set('tab', view.tab); } else { params.delete('tab'); }
  return searchOf(params);
}

/** The search string without a workspace tab (`?tab=apps|…`, `?history=`) and the params only a workspace tab has. */
function dropWorkspaceTab(search) {
  const params = new URLSearchParams(search || '');
  params.delete('history');
  if (params.get('focus') === 'jobs') { params.delete('focus'); }
  if (params.get('section') === 'jobs') { params.delete('section'); }
  const tab = params.get('tab');
  if (VIEW_TABS.includes(tab) || tab === 'history' || CHANGES_SUB_IDS.includes(tab)) {
    params.delete('tab');
    params.delete('sub');
    // Its params go with it (`service` and `path` are the Logs and Code pages' too: they go only with the workspace tab).
    [...paramsOfTab(tab), 'file', 'section'].forEach((key) => params.delete(key));
  }
  const text = params.toString();
  return text ? `?${text}` : '';
}

/**
 * The search string with the Apps tab's selection: `&service=<name>` (the short name, or a pinned file's runner name)
 * and `&section=<sub-tab>`; null for either (the first service, Overview) leaves it out. Only while `?tab=apps`.
 */
export function searchWithAppsSelection(search, { service = null, section = null } = {}) {
  const params = new URLSearchParams(search || '');
  if (params.get('tab') !== 'apps') { return search || ''; }
  if (service) { params.set('service', service); } else { params.delete('service'); }
  if (section && section !== 'overview') { params.set('section', section); } else { params.delete('section'); }
  const text = params.toString();
  return text ? `?${text}` : '';
}

/**
 * Which tab is on: a workspace view (Changes, Activity and Sessions are History), else the page — Home
 * (Dashboard, or Code for its Files, 'logs' for Logs, which is a tool), a service (Code or Preview).
 */
export function currentWorkspaceTab({ view = null, atHome = true, activeTab = 'preview', homeMode = 'dashboard' } = {}) {
  const shown = normalizeView(view);
  if (shown && VIEW_TABS.includes(shown.tab)) { return HISTORY_IDS.includes(shown.tab) ? 'history' : shown.tab; }
  if (atHome) {
    if (homeMode === 'files') { return 'code'; }
    if (homeMode === 'logs') { return 'logs'; }
    return 'dashboard';
  }
  return activeTab === 'code' ? 'code' : 'preview';
}

const latestView = (person) => [...(person?.views || [])].sort((a, b) => (b.at || 0) - (a.at || 0))[0] || null;

/** The others looking at a preview right now (their latest view is a preview): the Preview tab shows them. */
export function previewWatchers(snapshot) {
  const you = snapshot?.you;
  return (snapshot?.people || [])
    .filter((person) => !you || person.id !== you.id)
    .filter((person) => latestView(person)?.kind === 'preview');
}

/** The Changes tab's count: the changes others (people or their agents, or your agents) made, from a changes answer. */
export function changesCountOf(answer) {
  const youId = answer?.you?.id;
  return (answer?.changes || []).filter((change) => !youId || change.author?.id !== youId || change.agent).length;
}

/** "Owner" / "Editor" / "Viewer" for the header's role chip, or null. */
export function roleLabel(role) {
  if (role === 'owner') { return 'Owner'; }
  if (role === 'editor') { return 'Editor'; }
  if (role === 'viewer') { return 'Viewer'; }
  return null;
}
